import { Button } from '@breetometer/components';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import type { PDFLoadingState, PDFViewerProps } from '../../FileInput/FileInput.helpers';
import { FilePreviewError } from './Error';
import { FilePreviewLoading } from './Loading';
import { getPdfLib } from './pdfjs.loader';

export const Pdf: FC<PDFViewerProps> = ({ pdfFile, width = 800, className = '' }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
	const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

	const [{ isLoading, error }, setLoadingState] = useState<PDFLoadingState>({
		isLoading: true,
		error: null,
	});

	const [numPages, setNumPages] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const objectUrlRef = useRef<string | null>(null);

	const setLoading = useCallback((loading: boolean) => {
		setLoadingState((prev) => ({ ...prev, isLoading: loading }));
	}, []);

	const setError = useCallback((errorMessage: string | null) => {
		setLoadingState((prev) => ({ ...prev, error: errorMessage }));
	}, []);

	const cleanupCanvas = useCallback(() => {
		if (containerRef.current) {
			containerRef.current.innerHTML = '';
		}
		if (renderTaskRef.current) {
			renderTaskRef.current.cancel();
			renderTaskRef.current = null;
		}
	}, []);

	const renderPage = useCallback(
		async (pdf: PDFDocumentProxy, pageNumber: number): Promise<void> => {
			if (!containerRef.current) return;

			try {
				if (renderTaskRef.current) {
					renderTaskRef.current.cancel();
				}

				const page: PDFPageProxy = await pdf.getPage(pageNumber);
				const viewport = page.getViewport({ scale: 1.0 });

				const canvas = document.createElement('canvas');
				canvas.setAttribute('data-testid', `pdf-canvas-${pageNumber}`);
				const context = canvas.getContext('2d');

				if (!context) {
					throw new Error('Could not get canvas 2D context');
				}

				canvas.height = viewport.height;
				canvas.width = viewport.width;

				cleanupCanvas();
				containerRef.current.appendChild(canvas);

				const renderTask = page.render({
					canvasContext: context,
					viewport: viewport,
					canvas,
				});

				renderTaskRef.current = renderTask;

				await renderTask.promise;

				renderTaskRef.current = null;
			} catch (renderError) {
				if (renderError instanceof Error && renderError.name !== 'RenderingCancelledException') {
					console.error('Page rendering error:', renderError);
					setError('Failed to render PDF page');
				}
			}
		},
		[cleanupCanvas, setError]
	);

	const loadPDF = useCallback(async (): Promise<void> => {
		let pdfSource: string | Uint8Array | undefined;

		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}

		if (typeof pdfFile === 'string') {
			pdfSource = pdfFile;
		} else if ('previewUrl' in pdfFile) {
			setLoading(true);
			setError(null);
			try {
				const objUrl = pdfFile.previewUrl;
				objectUrlRef.current = objUrl;
				pdfSource = objUrl;
			} catch (fileError) {
				console.error('Error creating object URL for PDF file:', fileError);
				setError('Failed to process local PDF file.');
				setLoading(false);
				return;
			}
		} else {
			setLoading(false);
			setError('No PDF source provided.');
			setNumPages(0);
			setCurrentPage(1);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const pdfjsLib = await getPdfLib();

			if (pdfDocRef.current) {
				await pdfDocRef.current.destroy();
				pdfDocRef.current = null;
			}

			const loadingTask = pdfjsLib.getDocument(pdfSource);

			const pdf = await loadingTask.promise;

			pdfDocRef.current = pdf;
			setNumPages(pdf.numPages);

			setCurrentPage(1);
			await renderPage(pdf, 1);
		} catch (loadError) {
			console.error('PDF loading error:', loadError);
			if (loadError instanceof Error) {
				if (loadError.message.includes('CORS') || loadError.name === 'NetworkError') {
					setError(
						'CORS Error: Cannot load PDF from external domain. Please ensure the PDF is on the same domain or a CORS proxy is used, or host the PDF locally.'
					);
				} else {
					setError(`Failed to load PDF: ${loadError.message}`);
				}
			} else {
				setError('Failed to load PDF: Unknown error');
			}
		} finally {
			setLoading(false);
		}
	}, [pdfFile, setLoading, setError, renderPage]);

	useEffect(() => {
		let cancelled = false;

		const loadPDFSafely = async (): Promise<void> => {
			await loadPDF();
			if (cancelled) return;
		};

		loadPDFSafely().catch((err: unknown) => {
			if (!cancelled) {
				console.error('PDF loading failed:', err);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [loadPDF]);

	useEffect(() => {
		let cancelled = false;

		const renderCurrentPage = async (): Promise<void> => {
			if (!pdfDocRef.current || currentPage < 1 || currentPage > numPages) {
				return;
			}

			try {
				await renderPage(pdfDocRef.current, currentPage);
				if (cancelled) return;
			} catch (renderError) {
				if (!cancelled) {
					console.error('Page render error:', renderError);
				}
			}
		};

		if (numPages > 0) {
			renderCurrentPage().catch((err: unknown) => {
				if (!cancelled) {
					console.error('Page rendering failed:', err);
				}
			});
		}

		return () => {
			cancelled = true;
		};
	}, [currentPage, numPages, renderPage]);

	useEffect(
		() => () => {
			cleanupCanvas();
			if (pdfDocRef.current) {
				pdfDocRef.current.destroy().catch(console.error);
			}
		},
		[cleanupCanvas]
	);

	const goToPage = useCallback(
		(pageNumber: number) => (): void => {
			if (pageNumber >= 1 && pageNumber <= numPages) {
				setCurrentPage(pageNumber);
			}
		},
		[numPages]
	);

	const onReload = useCallback((): void => {
		setError(null);
		loadPDF().catch(console.error);
	}, [setError, loadPDF]);

	if (isLoading) {
		return (
			<div className={`pdf-viewer-container ${className}`}>
				<FilePreviewLoading loadText='Loading PDF' />
			</div>
		);
	}

	if (error) {
		return (
			<div className={`pdf-viewer-container ${className}`}>
				<FilePreviewError
					error={error}
					onReload={onReload}
				/>
			</div>
		);
	}

	return (
		<div className={`pdf-viewer-container ${className}`}>
			<div className='pdf-controls'>
				<Button
					onClick={goToPage(currentPage - 1)}
					disabled={currentPage <= 1}
					className='nav-button'
					type='button'
					label='← Previous'
				/>

				<span className='page-info'>
					Page {currentPage} of {numPages}
				</span>

				<Button
					onClick={goToPage(currentPage + 1)}
					disabled={currentPage >= numPages}
					className='nav-button'
					type='button'
					label='Next →'
				/>
			</div>

			<div
				ref={containerRef}
				className='pdf-content'
				style={{ width: `${width}px`, maxWidth: '100%' }}
			/>
		</div>
	);
};
